import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { projectsAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import SectionHeading from './SectionHeading';

const EASE = [0.16, 1, 0.3, 1];

const ProjectsGrid = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const allProjects = await projectsAPI.getAll();
        setProjects(allProjects);
        setLoading(false);
      } catch (error) {
        console.error('Error loading projects:', error);
        setLoading(false);
      }
    };
    loadProjects();
  }, []);

  if (loading) {
    return (
      <section id="proyectos" className="bg-surface py-28">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white/40">Cargando proyectos...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="proyectos" className="bg-surface py-24 md:py-32">
      <div className="container mx-auto px-6 lg:px-10">
        <SectionHeading
          overline={t('projects.sectionTitle')}
          title={t('projects.title1')}
          accent={t('projects.title2')}
          subtitle={t('projects.subtitle')}
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.length === 0 ? (
            <div className="col-span-full text-center text-white/40 py-12">
              No hay proyectos disponibles
            </div>
          ) : (
            projects.slice(0, 6).map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: EASE, delay: (index % 3) * 0.1 }}
              >
                <Link
                  to="/proyectos"
                  data-testid={`home-project-${project.id}`}
                  className="group relative overflow-hidden rounded-2xl cursor-pointer block"
                >
                  <div className="relative h-[26rem] bg-surface-hi overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-brand/40 rounded-2xl transition-all duration-500" />

                    <div className="absolute bottom-0 left-0 right-0 p-7">
                      <p className="overline mb-2">{project.category}</p>
                      <h3 className="font-heading text-2xl font-light text-white mb-2 tracking-tight">{project.title}</h3>
                      <p className="text-white/60 text-sm max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100 overflow-hidden transition-all duration-500">
                        {project.subtitle}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))
          )}
        </div>

        <div className="text-center mt-16">
          <Link
            to="/proyectos"
            data-testid="projects-view-all"
            className="btn-ghost inline-flex items-center gap-2 px-8 py-4 rounded-sm text-sm uppercase tracking-wider font-medium group"
          >
            {t('projects.viewAll')}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProjectsGrid;
